/**
 * ==================== 视频图层模块 - FabricVideo 自定义类 ====================
 *
 * 职责：
 * 1. FabricVideo 类定义：继承 fabric.Image，重写 _render 绘制视频 UI
 * 2. 命名空间注册：将 FabricVideo 注册到 fabric 命名空间，支持 enlivenObjects 反序列化
 * 3. 命中检测：播放图标的点击区域计算和命中判断
 *
 * 依赖：./meta（getVideoLayerInfo 用于 _render 中提取视频信息）
 * 被依赖：factory.ts、interaction.ts、index.ts
 */

import { fabric } from "fabric";
import videoPlayIconUrl from "@/assets/images/board/video-play.png";
import videoCanvasIconUrl from "@/assets/images/board/icon-video-canvas.png";
import { getVideoLayerInfo } from "./meta";
import { resolveVideoCenterControlLocalSize, resolveVideoObjectDisplayScale } from "./constants";
import { useBoardLayerStore } from "../store/layer";

/** 惰性获取图层 store（fabric 渲染在组件上下文外，需防 pinia 未激活） */
let layerStoreRef: ReturnType<typeof useBoardLayerStore> | null = null;
const getPlayingVideoLayerId = (): string | null => {
  try {
    if (!layerStoreRef) layerStoreRef = useBoardLayerStore();
    return layerStoreRef.playingVideoLayerId;
  } catch {
    return null;
  }
};

/** 播放图标（预加载），模块级单例避免重复创建 Image 对象 */
const playIconImage = new Image();
playIconImage.src = videoPlayIconUrl;

/** 右下角视频图标（预加载） */
const videoCanvasIconImage = new Image();
videoCanvasIconImage.src = videoCanvasIconUrl;

/**
 * 自定义视频图层类
 * - 继承 fabric.Image，使用视频封面作为图像源
 * - 重写 _render 方法绘制视频 UI（名称、时长、播放图标）
 * - 注册到 fabric 命名空间以支持 enlivenObjects 反序列化
 */
class FabricVideo extends fabric.Image {
  public type = "video";

  public toObject(propertiesToInclude?: string[]) {
    const base = super.toObject(propertiesToInclude);
    return {
      ...base,
      type: "video",
      extraData: (this as any).extraData
    };
  }

  /**
   * 从 JSON 对象恢复视频图层（供 enlivenObjects 使用）
   * 直接引用 FabricVideo 类，避免通过 fabric 命名空间的循环引用
   */
  static fromObject(object: any, callback: (obj: fabric.Object) => void) {
    const src = object?.src || "";
    const crossOrigin = object?.crossOrigin || "anonymous";
    (fabric.util as any).loadImage(
      src,
      (img: HTMLImageElement | null) => {
        const element = img || new Image();
        (element as any).crossOrigin = crossOrigin;
        const instance = new FabricVideo(element, {
          ...object,
          crossOrigin
        });
        (instance as any).type = "video";
        (instance as any).extraData = object?.extraData;
        callback(instance);
      },
      undefined,
      crossOrigin
    );
  }

  /**
   * 重写渲染方法，在视频封面上绘制 UI 元素
   * - 底部渐变背景 + 视频时长（左下）+ 视频图标（右下）
   * - 中央播放图标
   */
  _render(ctx: CanvasRenderingContext2D) {
    // 播放弹窗打开时跳过静态封面绘制：透明 webm（alpha 视频）会透出弹窗下的封面导致重影
    if (getPlayingVideoLayerId() === this.id) return;
    super._render(ctx);

    const w = this.width || 0;
    const h = this.height || 0;
    const scaleX = Math.abs(this.scaleX ?? 1) || 1;
    const scaleY = Math.abs(this.scaleY ?? 1) || 1;
    const scale = Math.max(scaleX, scaleY, 1e-6);
    const info = getVideoLayerInfo(this);
    const duration = info?.duration || 0;

    const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

    ctx.save();

    // 底部渐变背景，使文字清晰可见
    const displayedHeight = h * scale;
    // 字体大小系数增大，使字体更大
    const fontSizePx = clamp(displayedHeight * 0.126, 28, 56);
    const fontSize = fontSizePx / scale;
    const gradientHeightPx = fontSizePx * 2.5;
    const gradientHeight = gradientHeightPx / scale;
    const grad = ctx.createLinearGradient(0, h / 2 - gradientHeight, 0, h / 2);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.6)");
    ctx.fillStyle = grad;
    ctx.fillRect(-w / 2, h / 2 - gradientHeight, w, gradientHeight);

    // 绘制文字
    ctx.fillStyle = "rgba(236, 233, 242, 0.90)";
    ctx.font = `400 ${fontSize}px sans-serif`;
    ctx.textBaseline = "bottom";
    ctx.shadowColor = "rgba(0, 0, 0, 0.30)";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1 / scale;

    // 左下角：视频时长
    const paddingPx = fontSizePx * 1.2;
    const offsetPx = fontSizePx * 0.8;
    const padding = paddingPx / scale;
    const offset = offsetPx / scale;
    ctx.textAlign = "left";
    ctx.fillText(formatDuration(duration), -w / 2 + padding, h / 2 - offset);

    // 重置阴影，避免影响后续绘制
    ctx.shadowColor = "transparent";
    ctx.shadowOffsetY = 0;

    // 右下角：视频图标
    if (videoCanvasIconImage.complete && videoCanvasIconImage.naturalWidth > 0) {
      const iconSizePx = fontSizePx * 1.3;
      const iconSize = iconSizePx / scale;
      ctx.drawImage(videoCanvasIconImage, w / 2 - padding - iconSize, h / 2 - offset - iconSize * 0.8, iconSize, iconSize);
    }

    // 中间：播放图标（短边比例 + 屏幕像素上下限，与暂停按钮同一套逻辑）
    if (playIconImage.complete && playIconImage.naturalWidth > 0) {
      const iconSize = resolveVideoCenterControlLocalSize(this);
      ctx.drawImage(playIconImage, -iconSize / 2, -iconSize / 2, iconSize, iconSize);
    }

    ctx.restore();
  }
}

/**
 * 格式化时长为 mm:ss 格式
 */
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}

// ========== 命名空间注册 ==========

let _fabricVideoRegistered = false;

/**
 * 确保 FabricVideo 类已注册到 fabric 命名空间
 * - 供给 enlivenObjects 反序列化时通过 type: "video" 查找对应的类
 * - 幂等操作，多次调用安全
 */
export const ensureFabricVideoRegistered = () => {
  if (_fabricVideoRegistered) return;
  // 如果已经被其他方式注册过（如热更新保留），则直接标记
  if ((fabric as any).Video?.prototype?.type === "video") {
    _fabricVideoRegistered = true;
    return;
  }
  (fabric as any).Video = FabricVideo;
  _fabricVideoRegistered = true;
};

// ========== 类型判断与命中检测 ==========

/**
 * 判断一个 fabric.Object 是否为视频图层
 * 供外部模块（interaction.ts、factory.ts）使用
 */
export const isVideoLayer = (obj: fabric.Object | undefined | null): boolean => {
  return (obj as any)?.type === "video";
};

/**
 * 计算视频图层播放图标的点击命中区域
 *
 * @param obj - fabric.Object 实例
 * @returns 包含半宽和半高的对象，或 null（非视频图层时）
 *
 * NOTE: 命中区域与绘制逻辑一致——按视频屏幕短边比例 + 上下限（同暂停按钮）
 */
export function getVideoPlayIconHitBounds(
  obj: fabric.Object,
): { halfWidth: number; halfHeight: number } | null {
  if (!isVideoLayer(obj)) return null;

  const iconSize = resolveVideoCenterControlLocalSize(obj);
  const screenScale = resolveVideoObjectDisplayScale(obj);

  // 容差随按钮屏幕尺寸变化：约按钮边长的 40%，并限制在 8~20 屏幕像素
  const buttonScreenPx = iconSize * screenScale;
  const fixedTolerancePx = Math.max(8, Math.min(20, buttonScreenPx * 0.4));
  const fixedTolerance = fixedTolerancePx / screenScale;
  const ratioTolerance = iconSize * 0.15;
  const half = iconSize / 2 + fixedTolerance + ratioTolerance;

  return { halfWidth: half, halfHeight: half };
}

/**
 * 检查点击坐标是否在播放图标区域内
 *
 * @param obj - fabric.Object 实例
 * @param localPoint - 相对于对象中心的本地坐标（由 toLocalPoint 转换得到）
 * @returns 是否命中播放图标
 *
 * NOTE: localPoint 由 interaction.ts 中的矩阵逆变换得到，
 * 已正确完成世界坐标 → 本地坐标的转换（含缩放、旋转、倾斜等），
 * 因此直接比较 localPoint 与 icon 的本地边界即可。
 * 使用矩形检测以匹配播放图标的实际形状。
 */
export function isClickOnPlayIcon(obj: fabric.Object, localPoint: { x: number; y: number }): boolean {
  const bounds = getVideoPlayIconHitBounds(obj);
  if (!bounds) return false;

  return Math.abs(localPoint.x) <= bounds.halfWidth && Math.abs(localPoint.y) <= bounds.halfHeight;
}

/**
 * 计算右下角视频图标的点击命中区域
 *
 * @param obj - fabric.Object 实例
 * @returns 包含命中区域位置和尺寸的对象，或 null（非视频图层时）
 *
 * NOTE: 保持与 _render 中绘制逻辑完全一致
 * - 绘制位置: ctx.drawImage(videoCanvasIconImage, w/2 - padding - iconSize, h/2 - offset - iconSize * 0.8, iconSize, iconSize)
 * - 中心点计算需与绘制位置匹配
 * - 扩大响应区域以提升用户体验
 *
 * NOTE: 当 scaleX !== scaleY（非等比拉伸）时，图标在画布上呈拉伸矩形，
 * 但命中检测在本地坐标系中使用正方形区域（与 _render 的绘制逻辑一致）。
 * 由于 interaction.ts 已使用矩阵逆变换正确转换本地坐标，非等比缩放下命中检测可正常工作。
 */
export function getVideoCanvasIconHitBounds(
  obj: fabric.Object,
): { x: number; y: number; size: number } | null {
  if (!isVideoLayer(obj)) return null;

  const w = obj.width || 0;
  const h = obj.height || 0;
  const scaleX = Math.abs(obj.scaleX ?? 1) || 1;
  const scaleY = Math.abs(obj.scaleY ?? 1) || 1;
  // 与 _render 保持一致：使用 max(scaleX, scaleY) 作为统一缩放因子
  const scale = Math.max(scaleX, scaleY, 1e-6);

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
  const displayedHeight = h * scale;
  // NOTE: 与 _render 方法中保持一致的字体大小计算（使用 0.126 系数，与底部渐变背景一致）
  const fontSizePx = clamp(displayedHeight * 0.126, 28, 56);
  const iconSizePx = fontSizePx * 1.3;
  const iconSize = iconSizePx / scale;
  const paddingPx = fontSizePx * 1.2;
  const offsetPx = fontSizePx * 0.8;
  const padding = paddingPx / scale;
  const offset = offsetPx / scale;

  // 右下角图标中心点（相对于对象中心）
  // 绘制位置: (w/2 - padding - iconSize, h/2 - offset - iconSize * 0.8)
  // 中心点 = 绘制位置 + 图标尺寸的一半
  const drawX = w / 2 - padding - iconSize;
  const drawY = h / 2 - offset - iconSize * 0.8;
  const centerX = drawX + iconSize / 2;
  const centerY = drawY + iconSize / 2;

  // 扩大响应区域：增加固定像素容差（转换为本地坐标）
  const fixedTolerancePx = 20; // 增加容差到20像素，更好地处理拉伸情况
  const expandedSize = iconSize + (fixedTolerancePx / scale) * 2;

  return { x: centerX, y: centerY, size: expandedSize };
}

/**
 * 检查点击坐标是否在右下角视频图标区域内
 *
 * @param obj - fabric.Object 实例
 * @param localPoint - 相对于对象中心的本地坐标（由矩阵逆变换转换得到）
 * @returns 是否命中右下角视频图标
 *
 * NOTE: localPoint 由 interaction.ts 中的矩阵逆变换得到，
 * 已正确完成世界坐标 → 本地坐标的转换（含缩放、旋转、倾斜等），
 * bounds.size 已包含扩展的容差区域。
 */
export function isClickOnVideoCanvasIcon(
  obj: fabric.Object,
  localPoint: { x: number; y: number },
): boolean {
  const bounds = getVideoCanvasIconHitBounds(obj);
  if (!bounds) return false;

  // 使用已扩展的尺寸的一半作为半轴长度
  const halfSize = bounds.size / 2;

  return (
    localPoint.x >= bounds.x - halfSize &&
    localPoint.x <= bounds.x + halfSize &&
    localPoint.y >= bounds.y - halfSize &&
    localPoint.y <= bounds.y + halfSize
  );
}
